import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import  LogoContainer from '../../../../../shared/components/LogoContainer';
import Spinner from '../../../../../shared/components/Spinner'
import InstructionsModal from '../../../../../shared/components/InstructionsModal';
import { uploadInvestments } from "../../../../../../app/store/slices/investmentSlice";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import { toast } from "react-toastify";

const InvestmentUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const fileInputRef = useRef();
  const { paymentAccountId, canvasId } = location.state || {};

  const [selectedCanvasId] = useState(
    location.state?.canvasId ||
    JSON.parse(localStorage.getItem('canvasData') || '{}')?._id ||
    ''
  );

  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);
  const accounts = useSelector(selectAccounts);


  useEffect(() => {
      if (selectedCanvasId || canvasId) {
        dispatch(fetchAccounts({ canvasId:selectedCanvasId, limit:1000 }));
      }
    }, [dispatch, selectedCanvasId]);

  const handleFile = (file) => {
    setSelectedFile(file);
    setUploadError("");
    setUploadSuccess("");
  };

  // Handle input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    setUploadError("");
    setUploadSuccess("");
    if (!selectedCanvasId) {
      setUploadError("No canvas selected.");
      return;
    }
    if (!selectedFile) {
      setUploadError("Please select a file to upload.");
      return;
    }
    setIsUploading(true);
    try {
      await dispatch(
        uploadInvestments({
          canvasId: selectedCanvasId,
          file: selectedFile,
        })
      ).unwrap();
      setUploadSuccess("Investment uploaded successfully!");
      setSelectedFile(null);
      navigate('/dashboard');
    } catch (err) {
      setUploadError(
        err?.message ||
        err?.toString() ||
        "Failed to upload Investments. Please check your file and try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

   const handleDownloadTemplate = () => {
     try {
       const sampleAccountName = accounts && accounts.length > 0 ? accounts[0].name : 'Investment Account';
 
       const templateData = [
         {
           assetName: 'Apple Inc.',
           ticker: 'AAPL',
           type: 'Stock',
           trade: 'buy',
           units: '10',
           cost: '1505.00',
           orderPrice: '150.00',
           currency: 'USD',
           date: '2024-01-15',
           paymentAccountId: sampleAccountName,
           notes: 'Initial purchase'
         },
         {
           assetName: 'Vanguard S&P 500 ETF',
           ticker: 'VOO',
           type: 'ETF',
           trade: 'buy',
           units: '5',
           cost: '2009.95',
           orderPrice: '400.00',
           currency: 'USD',
           date: '2024-02-01',
           paymentAccountId: sampleAccountName,
           notes: 'Monthly investment'
         },
         {
           assetName: 'Bitcoin',
           ticker: 'BTC-USD',
           type: 'Crypto',
           trade: 'buy',
           units: '0.1',
           cost: '4525.00',
           orderPrice: '45000.00',
           currency: 'USD',
           date: '2024-02-15',
           paymentAccountId: sampleAccountName,
           notes: 'Crypto diversification'
         }
       ];
 
       const csvContent = [
         'assetName,ticker,type,trade,units,cost,orderPrice,currency,date,paymentAccountId,notes',
         ...templateData.map(row =>
           `"${row.assetName}","${row.ticker}","${row.type}","${row.trade}",${row.units},${row.cost},${row.orderPrice},"${row.currency}","${row.date}","${row.paymentAccountId}","${row.notes}"`
         )
       ].join('\n');
 
       const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
       const url = window.URL.createObjectURL(blob);
       const a = document.createElement('a');
       a.href = url;
       a.download = `investments_template_${new Date().toISOString().split('T')[0]}.csv`;
       document.body.appendChild(a);
       a.click();
       document.body.removeChild(a);
       window.URL.revokeObjectURL(url);
 
       setShowInstructions(false)
     } catch (error) {
       toast.error('Failed to download template');
       console.error('Template download error:', error);
     }
   };
 

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <LogoContainer/>
      <div className="card-setup">
        <h2 className="txt-page-heading">Upload your Investments</h2>
        <p className="p-tag-below-heading text-center mb-8">
          Upload your file and import data. your file must match the column headers of the file in the instructions File Below.
          you can verify, correct and complete the details from your canvas
        </p>
        <div className="w-full flex flex-col items-center">
          <button
            type="button"
            className="btn-back w-64"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={isUploading}
          >
            Select File
          </button>
          <span className="text-gray-500 mb-4 font-medium">or</span>
          <div
            className={`rounded-md px-8 py-8 w-64 flex items-center justify-center text-center font-medium cursor-pointer border-2 transition
              ${dragActive ? "border-btn-primary bg-blue-50 text-blue-700" : "bg-gray-100 border-dashed border-btn-primary text-black"}`}
            style={{ minHeight: 60 }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {selectedFile
              ? (
                <span className="truncate w-full">
                  <span className="font-semibold">Selected:</span> {selectedFile.name}
                </span>
              )
              : dragActive
                ? "Drop File Here"
                : "Drag & Drop"
            }
          </div>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".csv,.xlsx,.xls,.json,.pdf"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          {/* Error/Success messages */}
          {uploadError && (
            <div className="text-red-600 text-sm font-semibold mt-4">{uploadError}</div>
          )}
          {uploadSuccess && (
            <div className="text-green-600 text-sm font-semibold mt-4">{uploadSuccess}</div>
          )}
          <button
            type="button"
            className="mt-4 text-btn-primary underline text-sm"
            onClick={() => setShowInstructions(true)}
          >
            View Upload Instructions
          </button>
        </div>
        <div className="flex flex-row justify-between items-center w-full mt-12">
          <button
            className="btn-back"
            onClick={() => navigate(-1)}
            type="button"
            disabled={isUploading}
          >
            Back
          </button>
          <button
            className="btn-continue"
            onClick={handleUpload}
            type="button"
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? (
              <Spinner text="Uploading..."/>
            ):(
              "Continue"
            )

              }
          </button>
        </div>
      </div>
      {/* Instructions Modal */}
      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        type="investments"
        onDownloadTemplate={handleDownloadTemplate}
      />
    </div>
  );
};

export default InvestmentUpload;